import type {Schemas} from 'api/dto.generated';
import {ebsApi} from 'store/ebsApi';
import type {WorkMetadata, WorkV4} from './types';
import {mapToWorkBaseDto, mapWorkBaseDto, mapWorkFlattenedDto} from './mappers';

const workApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getWork: build.query<
      WorkMetadata,
      {id: string; mock?: false},
      Schemas.WorkFlattenedDto
    >({
      keepUnusedDataFor: 60,
      query: ({id}) => `work/v2/${id}`,
      transformResponse: mapWorkFlattenedDto,
      providesTags: r => (r ? [{type: 'Work', id: r.work.id}] : []),
    }),

    getWorkByEan: build.query<WorkMetadata, string, Schemas.WorkFlattenedDto>({
      keepUnusedDataFor: 60,
      query: ean => `work/v2/ean/${ean}`,
      transformResponse: mapWorkFlattenedDto,
      providesTags: r => (r ? [{type: 'Work', id: r.work.id}] : []),
    }),

    getWorks: build.query<WorkMetadata[], string[], Schemas.WorkFlattenedDtos>({
      keepUnusedDataFor: 60,
      query: ids => ({url: `work/v2/id`, method: 'POST', body: {ids}}),
      transformResponse: ({works}) => {
        return (works ?? []).map(mapWorkFlattenedDto);
      },
      providesTags: result =>
        result
          ? [...result.map(({work}) => ({type: 'Work', id: work.id}) as const)]
          : [],
    }),

    saveWork: build.mutation<WorkV4, WorkV4, Schemas.WorkBaseDto>({
      query: work => ({
        url: `work/${work.id}`,
        method: 'PUT',
        body: mapToWorkBaseDto(work),
      }),
      transformResponse: mapWorkBaseDto,
      invalidatesTags: work => (work ? [{type: 'Work', id: work.id}] : []),
    }),
  }),
});

export const {
  useGetWorkQuery,
  useLazyGetWorkQuery,
  useGetWorkByEanQuery,
  useLazyGetWorksQuery,
  useSaveWorkMutation,
} = workApi;
